/**
 * Create AGI Event Page - Simple form to create manual prediction markets
 * Only accessible to @agi.live users
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Sparkles, 
  CheckCircle, 
  Loader,
  Calendar,
  FileText,
  HelpCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAgiAuthStore, agiApi } from '../store/agiAuthStore'


export default function CreateAgiEventPage() {
  const navigate = useNavigate()
  const { agiUser, isAgiAuthenticated, agiToken } = useAgiAuthStore()
  
  const [question, setQuestion] = useState('')
  const [description, setDescription] = useState('')
  const [resolutionRules, setResolutionRules] = useState('')
  const [bettingEndsAt, setBettingEndsAt] = useState('')
  const [resolutionTime, setResolutionTime] = useState('')
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Redirect if not AGI user
  useEffect(() => {
    if (!isAgiAuthenticated || !agiUser?.isAgi) {
      toast.error('AGI Events are only for @agi.live team members')
      navigate('/markets')
    }
  }, [isAgiAuthenticated, agiUser, navigate])
  
  // Set default dates
  useEffect(() => {
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(17, 0, 0, 0) // 5 PM tomorrow
    
    const dayAfter = new Date(tomorrow)
    dayAfter.setDate(dayAfter.getDate() + 1)
    
    setBettingEndsAt(tomorrow.toISOString().slice(0, 16))
    setResolutionTime(dayAfter.toISOString().slice(0, 16))
  }, [])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!question.trim() || !resolutionRules.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Set auth header
      agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
      
      const response = await agiApi.post('/api/agi/markets', {
        question: question.trim(),
        description: description.trim(),
        resolutionRules: resolutionRules.trim(),
        bettingEndsAt: new Date(bettingEndsAt).toISOString(),
        resolutionTime: new Date(resolutionTime).toISOString()
      })
      
      if (response.data.success) {
        toast.success('AGI Event created! 🎯')
        
        // Navigate to the market immediately (AI Insight will show as a tab there)
        navigate(`/markets/${response.data.marketId}`)
      }
    } catch (error: any) {
      console.error('Create AGI event error:', error)
      toast.error(error.response?.data?.error || 'Failed to create event')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  if (!agiUser?.isAgi) {
    return null
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-[#001233] border border-[#052393]/40 text-gray-400 hover:text-white hover:border-[#2AAAEA]/50 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              <h1 className="text-2xl font-bold text-white">Create AGI Event</h1>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Internal prediction market for the @agi.live team
            </p>
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question */}
          <div className="bg-[#001233] border border-[#052393]/40 rounded-xl p-6">
            <label className="flex items-center space-x-2 text-white font-semibold mb-3">
              <HelpCircle className="w-5 h-5 text-cpx-light-blue" />
              <span>Prediction Question</span>
              <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Will we ship Feature X by Friday?"
              required
              maxLength={200}
              className="w-full bg-[#052393]/20 border border-[#052393]/50 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
            />
            <p className="text-xs text-gray-500 mt-2">
              Make it a clear YES/NO question. {question.length}/200 characters
            </p>
          </div>
          
          {/* Description */}
          <div className="bg-[#001233] border border-[#052393]/40 rounded-xl p-6">
            <label className="flex items-center space-x-2 text-white font-semibold mb-3">
              <FileText className="w-5 h-5 text-cpx-light-blue" />
              <span>Description</span>
              <span className="text-gray-500">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Add context about what this prediction is about..."
              rows={3}
              maxLength={1000}
              className="w-full bg-[#052393]/20 border border-[#052393]/50 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all resize-none"
            />
          </div>
          
          {/* Resolution Rules */}
          <div className="bg-[#001233] border border-[#052393]/40 rounded-xl p-6">
            <label className="flex items-center space-x-2 text-white font-semibold mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span>Resolution Rules</span>
              <span className="text-red-400">*</span>
            </label>
            <textarea
              value={resolutionRules}
              onChange={e => setResolutionRules(e.target.value)}
              placeholder="How will we determine YES or NO? Be specific!

Example:
- YES if PR #123 is merged to main AND deployed to production
- NO if the above conditions are not met by the resolution time"
              required
              rows={4}
              maxLength={1000}
              className="w-full bg-[#052393]/20 border border-[#052393]/50 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              Be specific! This is how the admin will decide the outcome.
            </p>
          </div>
          
          {/* Timing */}
          <div className="bg-[#001233] border border-[#052393]/40 rounded-xl p-6">
            <label className="flex items-center space-x-2 text-white font-semibold mb-4">
              <Calendar className="w-5 h-5 text-purple-400" />
              <span>Timing</span>
            </label>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Betting Closes <span className="text-red-400">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={bettingEndsAt}
                  onChange={e => setBettingEndsAt(e.target.value)}
                  required
                  className="w-full bg-[#052393]/20 border border-[#052393]/50 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Resolution Time <span className="text-red-400">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={resolutionTime}
                  onChange={e => setResolutionTime(e.target.value)}
                  required
                  className="w-full bg-[#052393]/20 border border-[#052393]/50 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
                />
              </div>
            </div>
          </div>
          
          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting || !question.trim() || !resolutionRules.trim()}
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Creating Event...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>Create AGI Event</span>
              </>
            )}
          </button>
        </form>
        
        {/* Tips */}
        <div className="mt-8 bg-[#052393]/20 border border-[#052393]/40 rounded-xl p-6">
          <h3 className="text-sm font-bold text-gray-300 mb-3">💡 Tips for good predictions:</h3>
          <ul className="text-sm text-gray-400 space-y-2">
            <li>• Make it specific and measurable</li>
            <li>• Set clear resolution criteria</li>
            <li>• Give enough time for betting</li>
            <li>• Fun predictions get more engagement!</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
